import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { workspacePathForRegistrationType } from '../utils/registrationTypes';
import { isSuperAdminUser } from '../utils/userRoles';
import { hasSeenLanding, markLandingSeen } from '../utils/landingVisitStorage';
import {
  AlertTriangle,
  Building2,
  Landmark,
  ArrowRight,
  Loader2,
  Newspaper,
  Table2,
  Cpu,
} from 'lucide-react';
import { getLandingIndiaCriticalMineralsNews } from '../services/pestleApi';
import { getRecentKabilDevelopments } from '../services/kabilApi';
import { SEMICONDUCTOR_CRITICALITY } from '../data/semiconductorCriticality';
import TableHeaderInfo from '../components/TableHeaderInfo';

const MOU_COLUMN_TIPS = {
  date: 'Date the MoU or partnership milestone was announced or last updated.',
  country: 'Partner country involved in the offshore resource or processing arrangement.',
  partner: 'Counterparty entity (state enterprise, miner, or joint venture) in the deal.',
  minerals: 'Critical minerals covered under the agreement (e.g. lithium, cobalt, REE).',
  stage: 'Current phase: exploration, offtake, JV formation, due diligence, or signed MoU.',
  quantity: 'Indicative annual quantity in tonnes per annum (tpa) where disclosed.',
};

const SEMICON_COLUMN_TIPS = {
  mineral: 'Input material or element used in semiconductor manufacturing.',
  role: 'Where this input sits in the fab value chain (wafer, packaging, chemicals, etc.).',
  importDependency: 'India’s reliance on imports for this input as a share of domestic demand.',
  supplyConcentration: 'How concentrated global supply is among top producing/exporting countries.',
  criticalityIndex: 'Composite score (1–5) reflecting strategic importance and supply fragility.',
  riskDriver: 'Primary factor driving risk: geopolitics, processing bottleneck, or demand surge.',
};

const CHALLENGES = [
  'Supply chains concentrated in a handful of countries, amplifying geopolitical risk',
  'Rapid demand growth from clean energy, EVs, and electronics outpaces new mine supply',
  'Export controls, sanctions, and resource nationalism reshape trade flows overnight',
  'Limited domestic reserves and processing capacity increase import dependency',
  'Price volatility and opaque market data complicate long-term planning',
];

const FOCUS_MINERALS = 'lithium, cobalt, nickel, graphite, and rare earths';

const STRATOSPHERE_SOLUTIONS = [
  `Dedicated coverage of ${FOCUS_MINERALS} — the minerals that power India’s EV, battery, and clean-energy supply chains`,
  'Trade, import dependency, and supplier concentration analytics for each focus mineral from India’s perspective',
  'PESTLE-analyzed news and alerts filtered to your five minerals and India-relevant geopolitical developments',
  'KAABIL/MoU and partnership tracking tied to lithium, cobalt, nickel, graphite, and rare earth engagements',
  'Supply-chain and policy dashboards so corporates and policymakers see exposure by mineral, partner country, and risk level',
];

function criticalityColor(index) {
  if (index >= 9) return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20';
  if (index >= 8) return 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20';
  return 'text-yellow-700 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20';
}

function mapAnalyzedArticleToNewsAlert(article) {
  const minerals = Array.isArray(article.minerals)
    ? article.minerals.filter(Boolean).join(', ')
    : '';
  const contextParts = [
    article.pestle_category,
    article.signal_type,
    minerals ? `Minerals: ${minerals}` : '',
  ].filter(Boolean);

  return {
    id: article.id,
    title: article.headline || 'Critical minerals update',
    description: contextParts.join(' · ') || undefined,
    severity: article.pestle_risk_level || '—',
    date: article.date,
    source: article.source || 'Analyzed Articles',
    sourceUrl: article.source_url,
    minerals: article.minerals,
  };
}

function formatAlertDate(timestamp) {
  if (!timestamp) return '—';
  try {
    return new Date(timestamp).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [newsAlerts, setNewsAlerts] = useState([]);
  const [mouRows, setMouRows] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(true);
  const [loadingMou, setLoadingMou] = useState(true);

  const isSuperAdmin = isSuperAdminUser(user);
  const isWorkspaceUser =
    isAuthenticated &&
    !!user &&
    !isSuperAdmin &&
    (user.registration_type === 'corporate' ||
      user.registration_type === 'policy_maker');
  const workspacePath = isWorkspaceUser
    ? workspacePathForRegistrationType(user.registration_type)
    : '/';
  const userHasSeenLanding = isWorkspaceUser ? hasSeenLanding(user) : false;
  const showContinueButton = isWorkspaceUser && !userHasSeenLanding;

  const handleContinueToDashboard = () => {
    if (isWorkspaceUser) {
      markLandingSeen(user);
      navigate(workspacePath, { replace: true });
    }
  };

  useEffect(() => {
    let cancelled = false;

    const loadAlerts = async () => {
      setLoadingAlerts(true);
      try {
        const articles = await getLandingIndiaCriticalMineralsNews(10);
        if (!cancelled) {
          setNewsAlerts(articles.map(mapAnalyzedArticleToNewsAlert));
        }
      } catch {
        if (!cancelled) setNewsAlerts([]);
      } finally {
        if (!cancelled) setLoadingAlerts(false);
      }
    };

    const loadMou = async () => {
      setLoadingMou(true);
      try {
        const developments = await getRecentKabilDevelopments();
        if (!cancelled) {
          setMouRows((developments || []).slice(0, 5));
        }
      } catch {
        if (!cancelled) setMouRows([]);
      } finally {
        if (!cancelled) setLoadingMou(false);
      }
    };

    loadAlerts();
    loadMou();
    return () => {
      cancelled = true;
    };
  }, []);

  if (
    !authLoading &&
    isWorkspaceUser &&
    userHasSeenLanding &&
    workspacePath !== '/'
  ) {
    return <Navigate to={workspacePath} replace />;
  }

  if (authLoading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center text-gray-500">
        Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200">
      <div className="container mx-auto px-4 sm:px-6 py-8 md:py-12 max-w-6xl">
        {/* Hero / context */}
        <section className="mb-12">
          <p className="text-sm font-medium text-[#03396c] dark:text-blue-400 uppercase tracking-wide mb-2">
            Critical Minerals Intelligence
          </p>
          <h1 className="text-3xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
            The Critical Minerals Space — Challenges & How Stratosphere Helps
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
            Stratosphere focuses on five minerals essential to India’s energy transition:
            lithium, cobalt, nickel, graphite, and rare earths. These materials underpin
            batteries, EVs, grid storage, and advanced manufacturing. Global demand is
            accelerating while supply remains concentrated, leaving India exposed to
            geopolitical shocks, export controls, and price volatility across this core set.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                Key Challenges
              </h2>
              <ul className="space-y-3">
                {CHALLENGES.map((item) => (
                  <li
                    key={item}
                    className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed flex gap-2"
                  >
                    <span className="text-[#03396c] dark:text-blue-400 mt-1">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-lg border border-[#03396c]/30 dark:border-blue-800 bg-[#03396c]/5 dark:bg-blue-950/30 p-6">
              <h2 className="text-xl font-semibold mb-4 text-[#03396c] dark:text-blue-300">
                How Stratosphere Solves the Problem
              </h2>
              <ul className="space-y-3">
                {STRATOSPHERE_SOLUTIONS.map((item) => (
                  <li
                    key={item}
                    className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed flex gap-2"
                  >
                    <span className="text-[#03396c] dark:text-blue-400 mt-1">✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="text-gray-600 dark:text-gray-400 text-sm">
            Rather than spreading across every critical mineral, Stratosphere goes deep on
            lithium, cobalt, nickel, graphite, and rare earths — combining trade, policy,
            partnership, and risk signals into one platform for evidence-based decisions in
            India’s battery and clean-energy ecosystem.
          </p>
        </section>

        {/* Data panels */}
        <section className="mb-12 space-y-8">
          <h2 className="text-2xl font-bold border-b border-gray-200 dark:border-gray-700 pb-3">
            Live Intelligence Snapshot
          </h2>

          {/* News data — 10 alerts, 5 visible with internal scroll */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 px-6 py-4 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2 min-w-0">
                <Newspaper className="h-5 w-5 text-[#03396c] dark:text-blue-400 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold">News Data — Critical Mineral Alerts</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Analyzed articles · India perspective
                  </p>
                </div>
              </div>
              {newsAlerts.length > 5 && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Showing 5 · scroll for {newsAlerts.length - 5} more
                </span>
              )}
            </div>
            <div className="p-6">
              {loadingAlerts ? (
                <div className="flex items-center justify-center py-12 text-gray-500">
                  <Loader2 className="h-6 w-6 animate-spin mr-2" />
                  Loading alerts…
                </div>
              ) : newsAlerts.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-sm py-4">
                  No analyzed articles match critical minerals with an India perspective yet.
                  New items appear as the news pipeline processes incoming coverage.
                </p>
              ) : (
                <ol
                  className={`space-y-4 pr-2 ${
                    newsAlerts.length > 5 ? 'overflow-y-auto custom-scrollbar' : ''
                  }`}
                  style={
                    newsAlerts.length > 5
                      ? { maxHeight: 'calc(5 * 5.25rem + 4 * 1rem)' }
                      : undefined
                  }
                >
                  {newsAlerts.map((alert, idx) => (
                    <li
                      key={alert.id || idx}
                      className="flex gap-4 min-h-[5.25rem] pb-4 border-b border-gray-100 dark:border-gray-800 last:border-0 last:pb-0 shrink-0"
                    >
                      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#03396c]/10 dark:bg-blue-900/40 text-[#03396c] dark:text-blue-300 text-sm font-semibold flex items-center justify-center">
                        {idx + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {alert.title}
                          </span>
                          {alert.severity && alert.severity !== '—' && (
                            <span className="text-xs px-2 py-0.5 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400">
                              {alert.severity}
                            </span>
                          )}
                        </div>
                        {alert.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                            {alert.description}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          {formatAlertDate(alert.date)}
                          {alert.source ? ` · ${alert.source}` : ''}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>

          {/* 5 MoU data points */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="flex items-center gap-2 px-6 py-4 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <Table2 className="h-5 w-5 text-[#03396c] dark:text-blue-400" />
              <h3 className="text-lg font-semibold">Latest MoU — 5 Data Points</h3>
            </div>
            <div className="p-6 overflow-x-auto">
              {loadingMou ? (
                <div className="flex items-center justify-center py-12 text-gray-500">
                  <Loader2 className="h-6 w-6 animate-spin mr-2" />
                  Loading MoU data…
                </div>
              ) : mouRows.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  No recent KAABIL/MoU developments found.
                </p>
              ) : (
                <table className="w-full text-sm text-left">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400">
                      <TableHeaderInfo
                        className="py-3 pr-4 font-medium"
                        label="Date"
                        tip={MOU_COLUMN_TIPS.date}
                      />
                      <TableHeaderInfo
                        className="py-3 pr-4 font-medium"
                        label="Country"
                        tip={MOU_COLUMN_TIPS.country}
                      />
                      <TableHeaderInfo
                        className="py-3 pr-4 font-medium"
                        label="Partner"
                        tip={MOU_COLUMN_TIPS.partner}
                      />
                      <TableHeaderInfo
                        className="py-3 pr-4 font-medium"
                        label="Minerals"
                        tip={MOU_COLUMN_TIPS.minerals}
                      />
                      <TableHeaderInfo
                        className="py-3 pr-4 font-medium"
                        label="Stage"
                        tip={MOU_COLUMN_TIPS.stage}
                      />
                      <TableHeaderInfo
                        className="py-3 font-medium"
                        label="Quantity (tpa)"
                        tip={MOU_COLUMN_TIPS.quantity}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {mouRows.map((row, idx) => (
                      <tr
                        key={row.id || idx}
                        className="border-b border-gray-100 dark:border-gray-800 last:border-0"
                      >
                        <td className="py-3 pr-4 whitespace-nowrap">
                          {row.formattedDate || row.date || '—'}
                        </td>
                        <td className="py-3 pr-4">{row.country || '—'}</td>
                        <td className="py-3 pr-4">{row.partner || '—'}</td>
                        <td className="py-3 pr-4">
                          {Array.isArray(row.minerals)
                            ? row.minerals.join(', ')
                            : row.minerals || '—'}
                        </td>
                        <td className="py-3 pr-4">{row.stage || '—'}</td>
                        <td className="py-3">
                          {row.quantity_tpa != null ? row.quantity_tpa : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-4">
                Source: KAABIL / KABIL partnership tracker
              </p>
            </div>
          </div>

          {/* Semiconductor criticality — 5 rows visible with internal scroll */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 px-6 py-4 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <Cpu className="h-5 w-5 text-[#03396c] dark:text-blue-400" />
                <h3 className="text-lg font-semibold">Semiconductor Criticality Data</h3>
              </div>
              {SEMICONDUCTOR_CRITICALITY.length > 5 && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Showing 5 · scroll for {SEMICONDUCTOR_CRITICALITY.length - 5} more
                </span>
              )}
            </div>
            <div className="p-6">
              <div
                className={`overflow-x-auto ${
                  SEMICONDUCTOR_CRITICALITY.length > 5
                    ? 'overflow-y-auto custom-scrollbar pr-2'
                    : ''
                }`}
                style={
                  SEMICONDUCTOR_CRITICALITY.length > 5
                    ? { maxHeight: 'calc(2.75rem + 5 * 4rem)' }
                    : undefined
                }
              >
                <table className="w-full text-sm text-left">
                  <thead className="sticky top-0 z-10 bg-white dark:bg-gray-900">
                    <tr className="border-b border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400">
                      <TableHeaderInfo
                        className="py-3 pr-4 font-medium bg-white dark:bg-gray-900"
                        label="Mineral / Input"
                        tip={SEMICON_COLUMN_TIPS.mineral}
                      />
                      <TableHeaderInfo
                        className="py-3 pr-4 font-medium bg-white dark:bg-gray-900"
                        label="Role in semiconductors"
                        tip={SEMICON_COLUMN_TIPS.role}
                      />
                      <TableHeaderInfo
                        className="py-3 pr-4 font-medium bg-white dark:bg-gray-900"
                        label="Import dependency"
                        tip={SEMICON_COLUMN_TIPS.importDependency}
                      />
                      <TableHeaderInfo
                        className="py-3 pr-4 font-medium bg-white dark:bg-gray-900"
                        label="Supply concentration"
                        tip={SEMICON_COLUMN_TIPS.supplyConcentration}
                      />
                      <TableHeaderInfo
                        className="py-3 pr-4 font-medium bg-white dark:bg-gray-900"
                        label="Criticality index"
                        tip={SEMICON_COLUMN_TIPS.criticalityIndex}
                      />
                      <TableHeaderInfo
                        className="py-3 font-medium bg-white dark:bg-gray-900"
                        label="Primary risk driver"
                        tip={SEMICON_COLUMN_TIPS.riskDriver}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {SEMICONDUCTOR_CRITICALITY.map((row) => (
                      <tr
                        key={row.mineral}
                        className="border-b border-gray-100 dark:border-gray-800 last:border-0 min-h-[4rem]"
                      >
                      <td className="py-3 pr-4 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                        {row.mineral}
                      </td>
                      <td className="py-3 pr-4 text-gray-700 dark:text-gray-300 max-w-[200px]">
                        {row.role}
                      </td>
                      <td className="py-3 pr-4">{row.importDependency}</td>
                      <td className="py-3 pr-4 text-gray-700 dark:text-gray-300 max-w-[180px]">
                        {row.supplyConcentration}
                      </td>
                      <td className="py-3 pr-4">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold ${criticalityColor(row.criticalityIndex)}`}
                        >
                          {row.criticalityIndex.toFixed(1)} / 10
                        </span>
                      </td>
                      <td className="py-3 text-gray-700 dark:text-gray-300">
                        {row.riskDriver}
                      </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* Persona cards — guests and Super Admin */}
        {(!isAuthenticated || isSuperAdmin) && (
        <section>
          <h2 className="text-2xl font-bold mb-6">Choose Your Path</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <button
              type="button"
              onClick={() => navigate('/corporate/alerts')}
              className="group text-left rounded-xl border-2 border-[#03396c] dark:border-blue-700 p-8 bg-white dark:bg-gray-800 hover:bg-[#03396c] hover:text-white dark:hover:bg-[#022a4d] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#03396c] focus:ring-offset-2 dark:focus:ring-offset-gray-900"
            >
              <div className="flex items-start justify-between gap-4">
                <Building2 className="h-10 w-10 text-[#03396c] dark:text-blue-400 group-hover:text-white flex-shrink-0" />
                <ArrowRight className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-1" />
              </div>
              <h3 className="text-xl font-bold mt-4 mb-3 group-hover:text-white">
                Corporate
              </h3>
              <p className="text-gray-600 dark:text-gray-300 group-hover:text-blue-50 text-sm leading-relaxed">
              For enterprises seeking structured assessments of supply chain vulnerabilities, cross-border regulatory exposure, and geopolitical risk — tailored to support executive-level decision-making.
              </p>
              <span className="inline-block mt-4 text-sm font-semibold text-[#03396c] dark:text-blue-400 group-hover:text-white">
                Explore corporate intelligence →
              </span>
            </button>

            <button
              type="button"
              onClick={() => navigate('/policy-maker')}
              className="group text-left rounded-xl border-2 border-[#03396c] dark:border-blue-700 p-8 bg-white dark:bg-gray-800 hover:bg-[#03396c] hover:text-white dark:hover:bg-[#022a4d] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#03396c] focus:ring-offset-2 dark:focus:ring-offset-gray-900"
            >
              <div className="flex items-start justify-between gap-4">
                <Landmark className="h-10 w-10 text-[#03396c] dark:text-blue-400 group-hover:text-white flex-shrink-0" />
                <ArrowRight className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-1" />
              </div>
              <h3 className="text-xl font-bold mt-4 mb-3 group-hover:text-white">
                Policy Maker
              </h3>
              <p className="text-gray-600 dark:text-gray-300 group-hover:text-blue-50 text-sm leading-relaxed">
              For government officials and regulatory bodies requiring a comprehensive overview of geopolitical dynamics — with scenario analyses and risk assessments to inform national and multilateral policy.
              </p>
              <span className="inline-block mt-4 text-sm font-semibold text-[#03396c] dark:text-blue-400 group-hover:text-white">
                Explore policy intelligence →
              </span>
            </button>
          </div>
        </section>
        )}

        {/* First-time Corporate / Policy Maker users see a Continue to Dashboard CTA */}
        {showContinueButton && (
          <section className="mt-4">
            <div className="rounded-xl border-2 border-[#03396c] dark:border-blue-700 bg-[#03396c]/5 dark:bg-blue-950/30 p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  Ready to dive in?
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Open your personalised{' '}
                  {user.registration_type === 'policy_maker'
                    ? 'Policy Maker'
                    : 'Corporate'}{' '}
                  alerts dashboard. You can revisit this overview any time from
                  the home link.
                </p>
              </div>
              <button
                type="button"
                onClick={handleContinueToDashboard}
                className="inline-flex items-center justify-center gap-2 bg-[#03396c] hover:bg-[#022a4d] text-white font-semibold py-3 px-6 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#03396c] focus:ring-offset-2 dark:focus:ring-offset-gray-900 shrink-0"
              >
                Continue to Dashboard
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default LandingPage;
